import { useState, useEffect, useCallback } from 'react';
import {
  getUnreadCommentCount,
  getUnreadComments,
  markCommentAsRead,
  markAllCommentsAsRead
} from '../apiClient';
import type { SquareComment } from '../types';

interface UseNotificationsOptions {
  /** 轮询间隔（毫秒），默认 60000 (1分钟) */
  pollingInterval?: number;
  /** 是否启用轮询，默认 true */
  enablePolling?: boolean;
  /** 是否已登录 */
  isLoggedIn?: boolean;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { pollingInterval = 60000, enablePolling = true, isLoggedIn = false } = options;

  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<SquareComment[]>([]);
  const [loading, setLoading] = useState(false);

  // 获取未读数量
  const fetchUnreadCount = useCallback(async () => {
    if (!isLoggedIn) return;

    try {
      const result = await getUnreadCommentCount();
      setUnreadCount(result.count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [isLoggedIn]);

  // 获取未读评论列表
  const fetchNotifications = useCallback(async () => {
    if (!isLoggedIn) return;

    setLoading(true);
    try {
      const result = await getUnreadComments(50);
      setNotifications(result.comments);
      setUnreadCount(result.comments.length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  // 标记单条为已读
  const markAsRead = useCallback(async (commentId: string) => {
    try {
      await markCommentAsRead(commentId);
      setNotifications(prev => prev.filter(n => n.id !== commentId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  }, []);

  // 标记全部为已读
  const markAllAsRead = useCallback(async () => {
    try {
      await markAllCommentsAsRead();
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  }, []);

  // 初始加载
  useEffect(() => {
    if (isLoggedIn) {
      fetchUnreadCount();
    }
  }, [isLoggedIn, fetchUnreadCount]);

  // 轮询
  useEffect(() => {
    if (!enablePolling || !isLoggedIn) return;

    const interval = setInterval(fetchUnreadCount, pollingInterval);
    return () => clearInterval(interval);
  }, [enablePolling, pollingInterval, fetchUnreadCount, isLoggedIn]);

  return {
    unreadCount,
    notifications,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    refresh: fetchUnreadCount,
  };
}
